import React, {
  createContext, useContext, useEffect, useState, useMemo,
  useCallback,
} from 'react';
import {
  getAuth, onAuthStateChanged, signInWithPopup, GoogleAuthProvider,
} from 'firebase/auth';
import { startSso } from 'sso/ssoClient';
import SessionChangedAlert from 'sso/SessionChangedAlert';

const AuthContext = createContext();

export function AuthProvider({ app, ssoEnabled, children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStateKnown, setAuthStateKnown] = useState(false);
  const [ssoReady, setSsoReady] = useState(false);
  const [switchUser, setSwitchUser] = useState(null);
  const initializing = !authStateKnown || !ssoReady;
  const auth = getAuth(app);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthStateKnown(true);
    });
    return unsubscribe;
  }, [auth]);

  // Cross-app single sign-on: keeps the app loading until a shared session is picked up.
  useEffect(() => startSso({
    auth,
    enabled: ssoEnabled,
    onReady: () => setSsoReady(true),
    onUserChange: (switchToSharedUser) => setSwitchUser(() => switchToSharedUser),
  }), [auth, ssoEnabled]);

  const signInWithGoogle = useCallback(() => {
    setLoading(true);
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider).finally(() => {
      setLoading(false);
    });
  }, [auth]);

  const signInCheck = useMemo(() => ({
    status: initializing ? 'loading' : 'success',
    data: {
      signedIn: !!currentUser,
      user: currentUser,
    },
  }), [currentUser, initializing]);

  const value = useMemo(() => ({
    currentUser,
    loading,
    initializing,
    signInWithGoogle,
    signInCheck,
  }), [currentUser, loading, initializing, signInWithGoogle, signInCheck]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionChangedAlert switchUser={switchUser} />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useSignInCheck() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useSignInCheck must be used within an AuthProvider');
  }

  return {
    status: context.signInCheck.status,
    data: context.signInCheck.data,
  };
}
